import React, { useEffect, useRef } from 'react';

/* React Redux */
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import { Grid, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';

/* Project */
import { anyFalseInObject, anyTruhtyInObject } from 'utils/functions';
import { darkerGreyOnForms } from 'utils/helper';
import {
  controlsChanged,
  getUserVehicleWithConectaApi,
  setControlsErrors,
} from '../../schedulerSteps.actions';
import SchedulerStepsStyles from '../../SchedulerSteps.styles';

const SEARCHING_MESSAGE = 'Buscando datos del vehículo';

const searchingHelperText = (searchingVehicle) => (searchingVehicle ? SEARCHING_MESSAGE : '');

const patentHelperText = (patentError, searchingVehicle) =>
  patentError || searchingHelperText(searchingVehicle);

// TODO: SET FIRENT MESSAGE ON PATENT ERRORS
const patentRequiredFieldValidation = (patent) => {
  const validations = {
    notEmptyValue: patent === '',
    notEmptyValueWithTrim: patent.trim().length === 0,
  };

  const isInvalidField = anyTruhtyInObject(validations);

  return isInvalidField ? 'Campo patente requerido' : '';
};

const patentFormatRequiredFieldValidation = (patent) => {
  const validations = {
    minLengthValidation: patent?.toString()?.length >= 6,
  };

  const isInvalidField = anyFalseInObject(validations);

  return isInvalidField ? 'Campo patente inválido' : '';
};

const isValidPatent = (patent) =>
  [patentRequiredFieldValidation, patentFormatRequiredFieldValidation].every(
    (validation) => validation(patent) === '',
  );

const shouldSearchPatent = (patent, lastSearchedPatent) =>
  isValidPatent(patent) && patent !== lastSearchedPatent;

function FindVehicleSection() {
  const dispatch = useDispatch();
  const patentState = useSelector((state) => state.schedulerSteps.controls.patent);
  const patentError = useSelector((state) => state.schedulerSteps.controlsErrors.patent);
  const scrollRef = useRef(null);
  const lastSearchedPatent = useRef('');

  const loading = useSelector((state) => state.schedulerSteps.loading);
  const searchingVehicle = useSelector((state) => state.schedulerSteps.searchingVehicle);

  useEffect(() => {
    if (patentError && scrollRef.current) {
      scrollRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }, [patentError]);

  const handleBlurPatentField = ({ name, value }) => {
    let errorMessage = '';
    errorMessage = patentRequiredFieldValidation(value);
    if (errorMessage === '') {
      errorMessage = patentFormatRequiredFieldValidation(value);
    }

    dispatch(
      setControlsErrors({
        controlsErrors: {
          [name]: errorMessage,
        },
      }),
    );
  };
  /* Plates are kept uppercase, as SAP registers them: the search, the summary and the
     scheduling all use the typed plate. */
  const onChange = (event) => {
    const { name } = event.target;
    const value = event.target.value.toUpperCase();
    dispatch(controlsChanged({ name, value }));
    if (name === 'patent') {
      handleBlurPatentField({ name, value });
    }
  };

  /* The search button calls it directly: it searches even an already searched plate, so the
     user can retry when SAP did not answer. */
  const searchPatent = () => {
    lastSearchedPatent.current = patentState;
    dispatch(getUserVehicleWithConectaApi({ patent: patentState }));
  };

  /* A valid plate is looked up to autocomplete the vehicle from SAP. The vehicle fields stay
     blocked until it answers, and no error is reported: without an answer the user just
     fills them manually. */
  const onBlur = () => {
    if (!shouldSearchPatent(patentState, lastSearchedPatent.current)) return;

    searchPatent();
  };

  return (
    <Grid
      ref={scrollRef}
      item
      xs={12}
      sm={12}
      md={5}
      lg={2.8}
      sx={{ ...SchedulerStepsStyles.searchContainer, marginBottom: '20px' }}>
      <TextField
        id="patent"
        name="patent"
        key="patent"
        label="Patente"
        placeholder="EJ: ABCD12"
        variant="outlined"
        InputLabelProps={{
          sx: { ...darkerGreyOnForms },
        }}
        size="small"
        sx={SchedulerStepsStyles?.patentField}
        fullWidth
        value={patentState}
        onChange={onChange}
        onBlur={onBlur}
        error={!!patentError}
        helperText={patentHelperText(patentError, searchingVehicle)}
        FormHelperTextProps={{ 'aria-live': 'polite' }}
        disabled={[loading, searchingVehicle].some(Boolean)}
      />
      <LoadingButton
        loading={searchingVehicle}
        color="lightBlue"
        variant="contained"
        aria-label="Buscar patente"
        disabled={[loading, !isValidPatent(patentState)].some(Boolean)}
        sx={SchedulerStepsStyles.searchButton}
        onClick={searchPatent}>
        <SearchIcon />
      </LoadingButton>
    </Grid>
  );
}

export default FindVehicleSection;
